import type { ActivityType } from '../types'

export type FieldType = 'text' | 'number' | 'datetime' | 'textarea' | 'select' | 'autocomplete'

export interface FormField {
  key: string
  label: string
  type: FieldType
  help?: string
  placeholder?: string
  options?: { value: string; label: string }[]
  half?: boolean // render two-up in a row
}

/**
 * Effort levels for resistance training, mapped to Compendium-of-Physical-Activities
 * MET values. Used to estimate calories burned from a set's duration.
 */
export const RESISTANCE_MET_OPTIONS = [
  { value: '3.5', label: 'Light — machines, easy effort' },
  { value: '5', label: 'Moderate — steady effort' },
  { value: '6', label: 'Vigorous — heavy / free weights' },
]

/** MET intensity options for general exercise. */
export const MET_OPTIONS = [
  { value: '1', label: '1 — Very Light' },
  { value: '2', label: '2 — Light' },
  { value: '3', label: '3 — Very Moderate' },
  { value: '4', label: '4 — Moderate' },
  { value: '5', label: '5 — Quite Vigorous' },
  { value: '6', label: '6 — Vigorous' },
  { value: '7', label: '7 — Really Vigorous' },
]

/** Fields driving the entry form, mirroring the legacy modals. */
export const FORM_FIELDS: Record<ActivityType, FormField[]> = {
  cardio: [
    { key: 'activity', label: 'Activity', type: 'autocomplete', help: 'The activity you performed.' },
    { key: 'datetime', label: 'Date/Time', type: 'datetime' },
    { key: 'level', label: 'Level', type: 'text', half: true },
    { key: 'gradient', label: 'Gradient', type: 'text', half: true },
    { key: 'duration', label: 'Duration', type: 'text', placeholder: 'HH:MM:SS' },
    { key: 'calories', label: 'Calories', type: 'number', half: true },
    { key: 'distance', label: 'Distance (KM)', type: 'number', half: true },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
  weights: [
    { key: 'operation', label: 'Operation', type: 'autocomplete', help: 'The lift / machine.' },
    { key: 'datetime', label: 'Date/Time', type: 'datetime' },
    { key: 'kg', label: 'Weight (KG)', type: 'number', half: true },
    { key: 'repetitions', label: 'Repetitions', type: 'number', half: true },
    { key: 'intensity', label: 'Effort', type: 'select', options: RESISTANCE_MET_OPTIONS, half: true },
    { key: 'duration', label: 'Duration', type: 'text', placeholder: 'HH:MM:SS', half: true },
    {
      key: 'calories',
      label: 'Calories',
      type: 'number',
      help: 'Estimated from load, reps + body weight; effort/duration refine it. Editable.',
    },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
  exercise: [
    { key: 'exercise', label: 'Exercise', type: 'autocomplete', help: 'The exercise you performed.' },
    { key: 'datetime', label: 'Date/Time', type: 'datetime' },
    { key: 'intensity', label: 'Intensity (MET)', type: 'select', options: MET_OPTIONS },
    { key: 'distance', label: 'Distance (KM)', type: 'number', half: true },
    { key: 'duration', label: 'Duration', type: 'text', placeholder: 'HH:MM:SS', half: true },
    { key: 'calories', label: 'Calories', type: 'number' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
}

/** Fields whose values can be prefilled from a prior entry of the same name. */
export const PREFILL_FIELDS = ['level', 'gradient', 'kg', 'repetitions', 'intensity']
